// Flou gaussien d'une modalité, réglé sur sa carte (et donc appliqué aussi avant le rapport ou la différence du canal
// calculé) : lisse le bruit pixel à pixel. Chaque tuile est floutée avec une marge prise dans ses voisines, pour éviter
// les raccords visibles aux bords des tuiles ; les tuiles décodées sont gardées en mémoire (cache LRU), de sorte que
// changer le rayon ne retélécharge rien.
import { pixelSize } from '../../services';

const SIGNAL_ABORTED = '__vivSignalAborted';  // valeur attendue par Viv pour une tuile abandonnée
const MAX_CACHED_TILES = 384;                 // ~100 Mo en 8 bits (tuiles 512 px)
const cache = new Map();                      // `${idSource}|${x}|${y}` -> Promise<tuile>
const sourceIds = new WeakMap();
let nextId = 1;

function cachedTile(source, x, y) {
  if (!sourceIds.has(source)) sourceIds.set(source, nextId++);
  const key = `${sourceIds.get(source)}|${x}|${y}`;
  let tile = cache.get(key);
  if (tile) {
    cache.delete(key);  // la plus récemment utilisée passe en fin de liste
  } else {
    tile = source.getTile({ x, y, selection: { t: 0, z: 0, c: 0 } });
    tile.catch(() => cache.delete(key));
  }
  cache.set(key, tile);
  while (cache.size > MAX_CACHED_TILES) cache.delete(cache.keys().next().value);
  return tile;
}

// Tailles de 3 flous de boîte successifs approchant une gaussienne d'écart-type sigma (Kutskir).
function boxSizes(sigma, n = 3) {
  const ideal = Math.sqrt((12 * sigma * sigma) / n + 1);
  let wl = Math.floor(ideal);
  if (wl % 2 === 0) wl -= 1;
  const wu = wl + 2;
  const m = Math.round((12 * sigma * sigma - n * wl * wl - 4 * n * wl - 3 * n) / (-4 * wl - 4));
  return Array.from({ length: n }, (_, i) => (i < m ? wl : wu));
}

// Moyenne glissante horizontale (rayon r), bords répliqués.
function boxH(src, dst, w, h, r) {
  const norm = 1 / (2 * r + 1);
  for (let y = 0; y < h; y += 1) {
    const row = y * w;
    let acc = src[row] * (r + 1);
    for (let i = 1; i <= r; i += 1) acc += src[row + Math.min(i, w - 1)];
    for (let x = 0; x < w; x += 1) {
      dst[row + x] = acc * norm;
      acc += src[row + Math.min(x + r + 1, w - 1)] - src[row + Math.max(x - r, 0)];
    }
  }
}

// Moyenne glissante verticale (rayon r), bords répliqués.
function boxV(src, dst, w, h, r) {
  const norm = 1 / (2 * r + 1);
  for (let x = 0; x < w; x += 1) {
    let acc = src[x] * (r + 1);
    for (let i = 1; i <= r; i += 1) acc += src[Math.min(i, h - 1) * w + x];
    for (let y = 0; y < h; y += 1) {
      dst[y * w + x] = acc * norm;
      acc += src[Math.min(y + r + 1, h - 1) * w + x] - src[Math.max(y - r, 0) * w + x];
    }
  }
}

export function gaussBlur(data, w, h, sigma) {
  let a = data;
  let b = new Float32Array(data.length);
  for (const size of boxSizes(sigma)) {
    const r = (size - 1) / 2;
    if (r < 1) continue;
    boxH(a, b, w, h, r);
    boxV(b, a, w, h, r);
  }
  return a;
}

// Tuile (x, y) d'un niveau entourée d'une marge de m px prise dans ses voisines (bords de l'image répliqués), en
// Float32 : { data, pw, ph } (avec la marge), { w, h } (tuile seule), center (tuile d'origine, pour son type).
// source : source Viv d'un niveau (getTile) ; size : { width, height } du niveau ; tileSize : côté des tuiles.
export async function paddedRegion(source, x, y, margin, size, tileSize, signal) {
  const { width: W, height: H } = size;
  const T = tileSize;
  const x0 = x * T; const y0 = y * T;
  const w = Math.min(T, W - x0); const h = Math.min(T, H - y0);
  const m = Math.min(T, margin);  // au plus une tuile voisine
  const tx0 = Math.max(0, Math.floor((x0 - m) / T)); const tx1 = Math.min(Math.ceil(W / T) - 1, Math.floor((x0 + w - 1 + m) / T));
  const ty0 = Math.max(0, Math.floor((y0 - m) / T)); const ty1 = Math.min(Math.ceil(H / T) - 1, Math.floor((y0 + h - 1 + m) / T));
  const nx = tx1 - tx0 + 1;
  const jobs = [];
  for (let ty = ty0; ty <= ty1; ty += 1) for (let tx = tx0; tx <= tx1; tx += 1) jobs.push(cachedTile(source, tx, ty));
  const tiles = await Promise.all(jobs);
  if (signal?.aborted) throw SIGNAL_ABORTED;

  // Tuile + marge (bords de l'image répliqués).
  const pw = w + 2 * m; const ph = h + 2 * m;
  const padded = new Float32Array(pw * ph);
  for (let j = 0; j < ph; j += 1) {
    const gy = Math.min(H - 1, Math.max(0, y0 - m + j));
    const ty = Math.floor(gy / T); const ly = gy - ty * T;
    for (let i = 0; i < pw; i += 1) {
      const gx = Math.min(W - 1, Math.max(0, x0 - m + i));
      const tx = Math.floor(gx / T);
      const tile = tiles[(ty - ty0) * nx + (tx - tx0)];
      padded[j * pw + i] = tile.data[ly * tile.width + (gx - tx * T)];
    }
  }
  return { data: padded, pw, ph, w, h, m, center: tiles[(y - ty0) * nx + (x - tx0)] };
}

// Tuile (x, y) d'un niveau, floutée avec un écart-type sigma (en pixels de ce niveau).
export async function blurredTile(source, x, y, sigma, size, tileSize, signal) {
  const { data: padded, pw, ph, w, h, m, center } = await paddedRegion(source, x, y, Math.ceil(3 * sigma), size, tileSize, signal);
  const blurred = gaussBlur(padded, pw, ph, sigma);
  const out = new center.data.constructor(w * h);
  const integer = !(out instanceof Float32Array || out instanceof Float64Array);
  const max = out instanceof Uint8Array ? 255 : out instanceof Uint16Array ? 65535 : Infinity;
  for (let j = 0; j < h; j += 1) {
    for (let i = 0; i < w; i += 1) {
      const v = blurred[(j + m) * pw + i + m];
      out[j * w + i] = integer ? Math.min(max, Math.max(0, Math.round(v))) : v;
    }
  }
  return { data: out, width: w, height: h };
}

// Niveau flouté : même interface qu'une source Viv (shape, labels, dtype, tileSize, getTile, getRaster) ; sigma en
// pixels de ce niveau (sous 0,3 px, flou invisible : tuiles d'origine).
class BlurSource {
  constructor(source, sigma) {
    this.source = source;
    this.sigma = sigma;
    this.shape = source.shape;
    this.labels = source.labels;
    this.dtype = source.dtype;
    this.tileSize = source.tileSize;
    this.meta = source.meta;
    this.size = pixelSize(source);
  }

  getTile({ x, y, selection, signal }) {
    if (this.sigma < 0.3) return this.source.getTile({ x, y, selection, signal });
    return blurredTile(this.source, x, y, this.sigma, this.size, this.tileSize, signal);
  }

  getRaster(args) { return this.source.getRaster(args); }

  onTileError(err) { this.source.onTileError?.(err); }
}

// Pyramide floutée d'une modalité mono-canal ; sigma en pixels pleine résolution, ramené à l'échelle de chaque niveau
// (même flou physique à tous les zooms : invisible de loin, net de près). perLevel : flou supplémentaire en pixels de
// chaque niveau, donc à peu près en pixels d'écran à tous les zooms (lissage anti-crénelage) ; deux flous gaussiens
// successifs font un flou d'écart-type hypot(σ1, σ2).
export function blurLoader(loader, sigma, perLevel = 0) {
  const width0 = pixelSize(loader[0]).width;
  return loader.map(level => new BlurSource(level, Math.hypot((sigma * pixelSize(level).width) / width0, perLevel)));
}
